import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';
import { uniq } from 'lodash';
import { on, reducer } from 'ts-action';
import * as ecgActions from '../../ecg/actions/ecg.actions';
import * as undoActions from '../../undo/actions/undo.actions';
import * as waveActions from '../actions/wave.actions';
import { Wave, WaveId } from '../types/wave.type';
import { getExtraTags } from '../utils/tags.utils';
import {
  getTypeFromTags,
  includeManual,
  sortWaves,
  toManualWave,
} from '../utils/wave.util';

export interface WaveState extends EntityState<Wave> {
  selectedIds: WaveId[];
  savedWaves: Wave[]; // this field will represent the saved waves of the user
}

declare module 'react-redux' {
  interface DefaultRootState {
    wave: WaveState;
  }
}

const waveAdapter = createEntityAdapter<Wave>({
  sortComparer: sortWaves,
});

export const { selectAll, selectEntities } = waveAdapter.getSelectors();

export default reducer<WaveState>(
  waveAdapter.getInitialState({ selectedIds: [], savedWaves: [] }),
  on(
    waveActions.setWaves,
    (state, { waves }): WaveState => waveAdapter.setAll(state, waves),
  ),
  on(
    waveActions.add,
    (state, { wave }): WaveState =>
      waveAdapter.upsertOne(state, toManualWave(wave)),
  ),
  on(
    waveActions.remove,
    (state, { waveIds }): WaveState => ({
      ...waveAdapter.removeMany(state, waveIds),
      selectedIds: state.selectedIds.filter((id) => !waveIds.includes(id)),
    }),
  ),
  on(
    waveActions.select,
    (state, { waveIds }): WaveState => ({
      ...state,
      selectedIds: uniq(state.selectedIds.concat(waveIds)),
    }),
  ),
  on(waveActions.setSavedWaves, (state, { waves }) => ({
    ...state,
    savedWaves: waves,
  })),
  on(waveActions.toggle, (state, { waveIds }): WaveState => {
    const selectedIds = new Set(state.selectedIds);
    waveIds.forEach((id) => selectedIds.delete(id) || selectedIds.add(id));
    return { ...state, selectedIds: Array.from(selectedIds) };
  }),
  on(waveActions.unselectAll, (state): WaveState => {
    if (state.selectedIds.length === 0) {
      return state;
    }

    return {
      ...state,
      selectedIds: [],
    };
  }),
  on(
    waveActions.moveToBackground,
    (state, { waveIds }): WaveState =>
      waveAdapter.updateMany(
        state,
        waveIds.map((waveId) => ({
          id: waveId,
          changes: { background: true },
        })),
      ),
  ),
  on(
    waveActions.moveToForeground,
    (state, { waveIds }): WaveState =>
      waveAdapter.updateMany(
        state,
        waveIds.map((waveId) => ({
          id: waveId,
          changes: { background: false },
        })),
      ),
  ),
  on(
    waveActions.setTags,
    (state, { waveIds, tags }): WaveState =>
      waveAdapter.updateMany(
        state,
        waveIds
          .map((waveId) => state.entities[waveId])
          .filter((wave): wave is Wave => wave != null)
          .map((wave: Wave) => {
            const extras = getExtraTags(wave.tags);
            const waveTags = includeManual(extras.concat(tags));
            return {
              id: wave.id,
              changes: {
                type: getTypeFromTags(wave.type, waveTags),
                tags: waveTags,
              },
            };
          }),
      ),
  ),
  on(
    ecgActions.clean,
    (): WaveState =>
      waveAdapter.getInitialState({ selectedIds: [], savedWaves: [] }),
  ),
  on(
    undoActions.undo,
    undoActions.restore,
    (state, { diff: { wave } }): WaveState => {
      if (!wave) {
        return state;
      }
      return { ...state, ...wave };
    },
  ),
);
